import { useState } from 'react';
import {
  Box, Typography, Paper, Avatar, Divider, Button, CircularProgress, useTheme, useMediaQuery
} from '@mui/material';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import { motion } from 'framer-motion';
import PrivacyConsentBox from './PrivacyConsentBox';

interface PhoneVerificationPanelProps {
  onVerified: (info: { name: string; phone: string }) => void;
  blocking: boolean;
  setBlocking: (val: boolean) => void;
}

const PhoneVerificationPanel = ({ onVerified, blocking, setBlocking }: PhoneVerificationPanelProps) => {
  const [agreed, setAgreed] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchCertificationInfo = async (imp_uid: string) => {
    try {
      const res = await fetch('https://us-central1-refurb-buy.cloudfunctions.net/getCertificationInfo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imp_uid }),
      });
      const data = await res.json();
      const { name, phone } = data;
      onVerified({ name, phone });
    } catch (e) {
      alert('본인인증 정보 수신 실패');
    } finally {
      setBlocking(false);
    }
  };

  const handleRealAuth = () => {
    const { IMP } = window as any;
    if (!IMP) return alert('IMP 로딩 실패');

    IMP.init('imp28577706');
    setIsAuthenticating(true);
    setBlocking(true);

    IMP.certification({
      pg: 'inicis_unified',
      channelKey: '66a216de-e4a7-4808-80dd-f95b7e7c96a6',
      merchant_uid: `mid_${Date.now()}`,
      m_redirect_url: `${window.location.href}`
    }, async (rsp: any) => {
      setIsAuthenticating(false);
      if (rsp.success) {
        await fetchCertificationInfo(rsp.imp_uid);
      } else {
        alert('인증 실패: ' + rsp.error_msg);
        setBlocking(false);
      }
    });
  };

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: 45, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.7 }}
      elevation={0}
      sx={{
        p: { xs: 3, md: 5 },
        borderRadius: 5,
        textAlign: 'center',
        background: 'rgba(255,255,255,0.62)',
        boxShadow: '0 4px 40px 0 rgba(91,68,246,0.13)',
        backdropFilter: 'blur(17px)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* floating blur element */}
      <Box sx={{
        position: 'absolute', top: -35, left: -35, width: 120, height: 120,
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        opacity: 0.17, filter: 'blur(34px)', borderRadius: '50%', zIndex: 0
      }} />
      <Box sx={{
        position: 'absolute', bottom: -24, right: -38, width: 110, height: 110,
        background: 'linear-gradient(135deg, #a5b4fc 0%, #f0abfc 100%)',
        opacity: 0.13, filter: 'blur(29px)', borderRadius: '50%', zIndex: 0
      }} />

      <Avatar
        sx={{
          width: isMobile ? 50 : 68, height: isMobile ? 50 : 68, mx: 'auto', mb: 2.3,
          bgcolor: 'transparent',
          background: 'linear-gradient(135deg, #0ea5e9 0%, #8b5cf6 100%)',
          boxShadow: '0 1px 16px 0 rgba(91,68,246,0.18)',
          border: '2.5px solid #fff',
        }}
      >
        <LockPersonIcon sx={{
          fontSize: isMobile ? 32 : 38,
          background: 'linear-gradient(135deg, #0ea5e9 0%, #8b5cf6 100%)',
          WebkitBackgroundClip: 'text',
          color: 'white'
        }} />
      </Avatar>
      <Typography
        variant={isMobile ? 'h6' : 'h5'}
        fontWeight={800}
        gutterBottom
        sx={{
          background: 'linear-gradient(90deg,#0ea5e9,#3b82f6 55%,#8b5cf6 100%)',
          WebkitBackgroundClip: 'text', color: 'transparent', letterSpacing: 0.7,
          mb: 1.2,
          zIndex: 1
        }}>
        본인 인증이 필요합니다
      </Typography>
      <Typography sx={{
        color: '#374151',
        fontWeight: 500,
        mb: 3,
        fontSize: isMobile ? 12 : 16,
        letterSpacing: 0.2,
        opacity: 0.92,
        zIndex: 1
      }}>
        안전한 서비스 이용을 위해 <b style={{ color: '#6366f1' }}>휴대폰 본인 인증</b>을 진행해주세요.
      </Typography>

      <PrivacyConsentBox agreed={agreed} onChange={setAgreed} />

      <Divider sx={{ my: 3, opacity: 0.25 }} />

      <Button
        variant="contained"
        onClick={handleRealAuth}
        disabled={!agreed || isAuthenticating}
        fullWidth
        sx={{
          fontWeight: 700,
          py: isMobile ? 1.2 : 1.5,
          px: 0,
          fontSize: isMobile ? 16 : 18,
          borderRadius: 99,
          background: 'linear-gradient(90deg, #0ea5e9 0%, #8b5cf6 100%)',
          boxShadow: '0 2px 18px 0 rgba(139,92,246,0.14)',
          transition: 'all 0.16s cubic-bezier(.4,2,.6,1)',
          mb: -1,
          mt: 1,
          '&:hover': {
            filter: 'brightness(1.08) saturate(1.07)',
            background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
            boxShadow: '0 5px 20px 0 rgba(91,68,246,0.15)',
          },
          '&.Mui-disabled': {
            background: 'linear-gradient(90deg, #e0e7ef 0%, #f1f5f9 100%) !important',
            color: '#b0b4bb !important',    // 텍스트 회색
            boxShadow: 'none',
            opacity: 1,
            cursor: 'not-allowed',
          }
        }}
        component={motion.button}
        whileTap={{ scale: 0.97 }}
        whileHover={{ scale: agreed && !isAuthenticating ? 1.022 : 1 }}
      >
        {isAuthenticating
          ? <CircularProgress size={22} color="inherit" sx={{ color: '#b0b4bb !important' }} />
          : '📱 휴대폰 인증하기'
        }
      </Button>

    </Paper>
  );
};

export default PhoneVerificationPanel;
